import { supabase } from "@/lib/supabase-client";
import { ContactType } from "@/types/db/contacts";
import { FileMetadata } from "@/types/files";

export const contactService = {
  async getContacts(): Promise<ContactType[]> {
    const { data, error } = await supabase.from("contacts").select(
      `
      id,
      first_name,
      last_name,
      preferred_name: meta_data->>preferred_name,
      email_address,
      phone_number,
      meta_data,
      created_at
      `,
    );
    if (error) throw new Error(error.message);
    return data as ContactType[];
  },

  async getContactById(id: string): Promise<ContactType | null> {
    // Fetch base contact data
    const { data, error } = await supabase.from("contacts").select("*").eq("id", id).single();
    if (error) {
      if (error.code === "PGRST116") {
        return null;
      }
      throw Error(error.message);
    }

    // Fetch contact_clients from link table
    const { data: contactClientsLink } = await supabase
      .schema("link")
      .from("clients_contacts")
      .select("id, note")
      .eq("contact_id", id);

    let contact_clients: { client: any; note: any }[] = [];
    if (contactClientsLink && contactClientsLink.length > 0) {
      // Get client IDs from link table (id column is the client_id)
      const clientIds = contactClientsLink.map((cc) => cc.id);

      // Fetch full client details
      const { data: clients } = await supabase
        .from("clients")
        .select("id, name, status, meta_data")
        .in("id", clientIds);

      // Map to contact_clients structure with notes
      contact_clients = (clients || []).map((client) => {
        const linkRecord = contactClientsLink.find((l) => l.id === client.id);
        return {
          client: {
            id: client.id,
            name: client.name,
            status: client.status,
            meta_data: client.meta_data,
          },
          note: linkRecord?.note || undefined,
        };
      });
    }

    return {
      ...data,
      contact_clients,
    } as ContactType;
  },

  async addContact(contactData: {
    first_name: string;
    last_name: string;
    email_address?: string;
    phone_number?: string;
    meta_data?: any;
    tenant_id: string;
  }): Promise<ContactType | null> {
    const { data, error } = await supabase.from("contacts").insert([contactData]).select().single();
    if (error) {
      console.error("Error adding Contact:", error.message);
      throw new Error(error.message);
    }
    return data as ContactType;
  },

  async updateContact(id: string, patch: Partial<ContactType>): Promise<ContactType> {
    const { data, error } = await supabase.from("contacts").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as ContactType;
  },

  async saveRowPatches(patches: Partial<ContactType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("contacts").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async deleteContact(id: string): Promise<boolean> {
    const { error } = await supabase.from("contacts").delete().eq("id", id);
    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  async deleteContactById(contactId: string): Promise<boolean> {
    const { error } = await supabase.from("contacts").delete().eq("id", contactId);
    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  async deleteContactsByIds(contactIds: string[]): Promise<boolean> {
    const { error } = await supabase.from("contacts").delete().in("id", contactIds);
    if (error) {
      console.error("Error deleting Contacts by IDs:", error.message);
      return false;
    }
    return true;
  },

  async syncContactClients(contactId: string, clients: { client: { id: string }; note?: string }[]): Promise<void> {
    // Get tenant_id from the contact record
    const { data: contactData, error: contactError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (contactError || !contactData) {
      throw new Error(`Failed to get contact tenant: ${contactError?.message || "Contact not found"}`);
    }

    const tenantId = contactData.tenant_id;

    // Delete all existing client links for this contact
    const { error: deleteError } = await supabase
      .schema("link")
      .from("clients_contacts")
      .delete()
      .eq("contact_id", contactId)
      .eq("tenant_id", tenantId);

    if (deleteError) {
      throw new Error(`Failed to delete existing contact clients: ${deleteError.message}`);
    }

    // Insert new client links
    if (clients.length > 0) {
      const records = clients.map((c) => ({
        id: c.client.id, // Client ID
        contact_id: contactId, // Contact ID
        tenant_id: tenantId, // Tenant ID
        note: c.note || null, // Optional note
      }));

      const { error: insertError } = await supabase.schema("link").from("clients_contacts").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert contact clients: ${insertError.message}`);
      }
    }
  },

  async getContactsByClientId(clientId: string): Promise<ContactType[]> {
    // Get contact IDs from the link table
    const { data: links, error: linkError } = await supabase
      .schema("link")
      .from("clients_contacts")
      .select("contact_id")
      .eq("id", clientId);

    if (linkError) {
      throw new Error(`Failed to fetch contact links for client ${clientId}: ${linkError.message}`);
    }

    if (!links || links.length === 0) return [];

    const contactIds = links.map((l: any) => l.contact_id);
    const { data, error } = await supabase
      .from("contacts")
      .select("*")
      .in("id", contactIds)
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch contacts for client ${clientId}: ${error.message}`);
    }

    return (data ?? []) as ContactType[];
  },

  // File management methods
  async uploadFile(contactId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from contact record
    const { data: tenantData, error: tenantError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get contact tenant: ${tenantError?.message || "Contact not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${contactId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("contacts").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "contacts", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(contactId: string): Promise<FileMetadata[]> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get contact tenant: ${tenantError?.message || "Contact not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${contactId}`;
    const { data: files, error: listError } = await supabase.storage.from("contacts").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list contact files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    const fileMetadata: FileMetadata[] = files.map((file) => {
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "contacts", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(contactId: string, fileId: string): Promise<boolean> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get contact tenant: ${tenantError?.message || "Contact not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${contactId}`;
    const { data: files, error: listError } = await supabase.storage.from("contacts").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list contact files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("contacts").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(contactId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get contact tenant: ${tenantError?.message || "Contact not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${contactId}`;
    const { data: files, error: listError } = await supabase.storage.from("contacts").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list contact files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("contacts").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "contacts",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  // Avatar management methods
  async uploadAvatar(contactId: string, file: File): Promise<string> {
    // Get tenant_id from contact record
    const { data: contactData, error: contactError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (contactError || !contactData) {
      throw new Error(`Failed to get contact tenant: ${contactError?.message || "Contact not found"}`);
    }

    // Get file extension
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filePath = `${contactData.tenant_id}/contacts/${contactId}.${extension}`;

    // Delete any existing avatar first
    try {
      await this.deleteAvatar(contactId);
    } catch (error) {
      // Ignore error if no existing avatar
      console.log("No existing avatar to delete or delete failed:", error);
    }

    // Upload new avatar to storage
    const { error: uploadError } = await supabase.storage.from("avatars").upload(filePath, file, {
      upsert: true,
    });

    if (uploadError) {
      throw new Error(`Avatar upload failed: ${uploadError.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);

    return urlData.publicUrl;
  },

  async deleteAvatar(contactId: string): Promise<boolean> {
    // Get tenant_id from contact record
    const { data: contactData, error: contactError } = await supabase
      .from("contacts")
      .select("tenant_id")
      .eq("id", contactId)
      .single();

    if (contactError || !contactData) {
      throw new Error(`Failed to get contact tenant: ${contactError?.message || "Contact not found"}`);
    }

    // Try to delete all possible avatar file extensions
    const extensions = ["jpg", "jpeg", "png", "gif", "webp"];
    const filePaths = extensions.map((ext) => `${contactData.tenant_id}/contacts/${contactId}.${ext}`);

    // Also check for files without extension
    filePaths.push(`${contactData.tenant_id}/contacts/${contactId}`);

    const { error } = await supabase.storage.from("avatars").remove(filePaths);

    if (error) {
      throw new Error(`Failed to delete avatar: ${error.message}`);
    }

    return true;
  },
};
